import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Gavel, AlertCircle, Loader2 } from 'lucide-react';

export default function StaffLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const data = await login(email, password);
      const role = data.user.role?.toLowerCase();
      if (role === 'admin') navigate('/admin');
      else if (role === 'lawyer') navigate('/lawyer');
      else if (role === 'agent') navigate('/agent');
      else navigate('/chat');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="staff-login-page">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-black rounded-sm flex items-center justify-center">
              <Gavel className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Instadrafts</h1>
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Staff Login</h2>
          <p className="text-sm text-gray-500 mb-8">Access the professional portal.</p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-sm flex items-center gap-3 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">Email Address</label>
              <input
                data-testid="staff-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black transition"
                placeholder="name@company.com"
                required
              />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">Password</label>
              <input
                data-testid="staff-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black transition"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              data-testid="staff-login-btn"
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-black text-white text-sm font-medium rounded-sm hover:bg-gray-800 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                'Login to Portal'
              )}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-100">
            <button 
              onClick={() => navigate('/login')}
              className="text-xs text-gray-400 hover:text-black transition uppercase tracking-widest font-mono"
            >
              ← Back to Client Login
            </button>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-black items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.1),_transparent_70%)]" />
        <div className="relative z-10 text-white px-12 max-w-lg text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight mb-4">Professional Access</h2>
          <p className="text-white/70 text-lg leading-relaxed font-light">Secure gateway for administrators, legal professionals, and service agents.</p>
        </div>
      </div>
    </div>
  );
}
